import { Box, Typography } from '@mui/material'
import { Prescription } from '../../../../types/models/Presc'
import { toCircledNum } from '../../../../helpers/toCircledNum'
import { colorTokens } from '../../../../shared/styles/theme'
import { isQrOnly } from '../list/queue'
import { FieldRow } from './FieldRow'
import { FeedbackRow } from './FeedbackRow'
import { RemoteInputRow } from './RemoteInputRow'
import { GigiAlerts } from './GigiAlerts'
import { Heading } from './Heading'
import { DetailFooter } from './DetailFooter'
import { useEditField } from './useEditField'
import { useReportField } from './useReportField'
import {
  BASIC_RESULT_ROWS,
  BIRTHDAY_HINT,
  REQUEST_GROUP_AT_BOTTOM,
  FIELD_CHOICES,
  FIELD_EDITORS,
  FIELD_LABELS,
  FieldGroup,
  REQUEST_FIELD_KEYS,
  formatBasicResultValue,
  validateFieldValue,
} from '../fields'

const Empty = () => (
  <Typography sx={{ py: 0.5, fontSize: 14, color: colorTokens.fg.faint }}>
    (なし)
  </Typography>
)

interface Props {
  prescription: Prescription
}

/**
 * 詳細の項目ペイン(画像の右)。**紙と 1 項目ずつ突き合わせ、違う欄をその場で直すか報告する**面。
 *
 * 並びは紙面の読み順(fields.ts の BASIC_RESULT_ROWS。保険が最上段)。8/28 の顧客の言葉
 * 「保険番号などの順番が処方箋と異なる為、非常に見にくい/流れで見るのが難しい」に効かせる。
 * 報告も修正も latest がキャッシュの更新を持つ(useReportField / useEditField)。送り先の形だけ
 * 共有 API(`apis/reportPresc` / `apis/editPresc`)を使う。共有の useReportCallbacks は呼ばない —
 * 成功のたびに一覧を全件取り直し、画像が再読み込みされるため(useReportField の docstring)。
 *
 * 保存が通らなかったときは、キャッシュの戻しを useEditField の onError が、入力の枠と文言を
 * EditableValue が持つ。ここは失敗を素通しするだけでよい。
 *
 * 直せるのは**保険・患者情報・用法/コメント**だけ(ADR-0032)。医薬品名と用量は直せない —
 * backend が薬品名から DB 最近傍でコードを引き直すため、打ち替えが別薬のコードになる。
 */
export const ItemsPane = ({ prescription }: Props) => {
  const { reportBasicResult, reportRPUnit, reportMedication, reportErrorOn } =
    useReportField(prescription)
  const { editBasicResult, editRpItem } = useEditField(prescription)

  const basic = prescription.basic_result
  const rpUnits = prescription.med_result ?? []

  /** 1 グループぶんの行。2 列に置く段はラベルを詰める(版面を保つため)。 */
  const renderGroup = (group: FieldGroup, isSideBySide: boolean) =>
    group.keys.map((key) => {
      const field = basic?.[key]
      if (!field) return null
      const isRequest = REQUEST_FIELD_KEYS.includes(key)
      const label = FIELD_LABELS[key] ?? key
      // 依頼の行は値を持たない。直せるかどうかは FIELD_EDITORS だけが決める。
      const editor = isRequest ? undefined : FIELD_EDITORS[key]
      return (
        <FieldRow
          key={key}
          label={label}
          labelWidth={isSideBySide ? 88 : 116}
          value={
            isRequest
              ? undefined
              : formatBasicResultValue(key, field.selected_option)
          }
          rawValue={field.selected_option}
          editor={editor}
          choices={FIELD_CHOICES[key]}
          hint={key === 'id_13_patient_birthday' ? BIRTHDAY_HINT : undefined}
          validate={(value) => validateFieldValue(key, value)}
          onSave={editor && ((value) => editBasicResult(key, value))}
          isReported={field.is_reported}
          onToggle={() => reportBasicResult(key, !field.is_reported)}
          note={reportErrorOn({ scope: 'basic', key })}
        />
      )
    })

  // 漏れた薬には行が無いので行の横では指せない。処方箋に1件だけ「どこかに漏れがある」を置く。
  // backend の器は RP 単位しか無いので RP1 に書く(04-apps/mamoru.md)。
  const firstRp = rpUnits[0]
  const isMedicationMissingReported = !!firstRp?.['医薬品取得']?.is_reported

  return (
    <Box sx={{ py: 2, px: 3 }}>
      {/* 「遠隔」行は一番上に置く(開いてすぐ札と操作が見え、項目をスクロールしなくても押せる)。
          項目の有無を問わず置く — 読み取りが空振りした紙も、オンなら依頼になり、遠隔の店が
          「断る」を押せる必要がある。出すかどうかは行が自分で決める */}
      <RemoteInputRow
        key={prescription.ocr_request_event_id}
        prescription={prescription}
      />

      {/* 疑義の段は項目の上(開いてすぐ入れ方の答えが読める。QuickSync の知らせを押して開いた紙も同じ段。ADR-0075) */}
      <GigiAlerts alerts={prescription.gigi_alerts ?? []} />

      {basic ? (
        BASIC_RESULT_ROWS.map((groups) => (
          <Box
            key={groups.map((group) => group.heading).join('/')}
            sx={{ display: 'flex', gap: 3 }}
          >
            {groups.map((group) => (
              <Box key={group.heading} sx={{ flex: 1, minWidth: 0 }}>
                <Heading>{group.heading}</Heading>
                {renderGroup(group, groups.length > 1)}
              </Box>
            ))}
          </Box>
        ))
      ) : isQrOnly(prescription) ? (
        // empty-states.md 型2「検知結果の空」= 実行した結果として項目が無い。
        // 段落文・イラスト・カード枠を付けない(1文だけ)。杏林堂では 57% がこの経路。
        <Typography sx={{ py: 1, fontSize: 14, color: colorTokens.fg.muted }}>
          JAHIS QR から直接読み取ったため、項目ごとの読み取り結果はありません
        </Typography>
      ) : (
        <Empty />
      )}

      {basic && (
        <Box>
          {rpUnits.length === 0 && (
            <>
              <Heading>処方内容</Heading>
              <Empty />
            </>
          )}
          {rpUnits.map((rp, rpIndex) => (
            <Box key={rpIndex}>
              <Heading>
                {`処方内容${toCircledNum(
                  Number(rp['RP番号'].selected_option)
                )}`}
              </Heading>
              {rp.med_result.map((med, medIndex) => (
                <Box key={medIndex}>
                  <FieldRow
                    label="医薬品名"
                    value={med['薬品名称'].selected_option}
                    isReported={med['薬品名称'].is_reported}
                    onToggle={() =>
                      reportMedication(
                        rpIndex,
                        medIndex,
                        '薬品名称',
                        !med['薬品名称'].is_reported
                      )
                    }
                    note={reportErrorOn({
                      scope: 'med',
                      rpIndex,
                      medIndex,
                      key: '薬品名称',
                    })}
                  />
                  <FieldRow
                    label="用量"
                    value={`${med['用量'].selected_option}${med['単位名'].selected_option}`}
                    isReported={med['用量'].is_reported}
                    onToggle={() =>
                      reportMedication(
                        rpIndex,
                        medIndex,
                        '用量',
                        !med['用量'].is_reported
                      )
                    }
                    note={reportErrorOn({
                      scope: 'med',
                      rpIndex,
                      medIndex,
                      key: '用量',
                    })}
                  />
                </Box>
              ))}
              {/* 用法とコメントは同じ調剤指示なので隣に置き、数量はその RP の締めとして最後に置く */}
              <FieldRow
                label="用法"
                value={rp['用法名称'].selected_option}
                editor="text"
                onSave={(value) => editRpItem(rpIndex, '用法名称', value)}
                isReported={rp['用法名称'].is_reported}
                onToggle={() =>
                  reportRPUnit(rpIndex, '用法名称', !rp['用法名称'].is_reported)
                }
                note={reportErrorOn({ scope: 'rp', rpIndex, key: '用法名称' })}
              />
              <FieldRow
                label="コメント"
                value={rp['コメント'].selected_option}
                editor="text"
                onSave={(value) => editRpItem(rpIndex, 'コメント', value)}
                isReported={rp['コメント'].is_reported}
                onToggle={() =>
                  reportRPUnit(rpIndex, 'コメント', !rp['コメント'].is_reported)
                }
                note={reportErrorOn({ scope: 'rp', rpIndex, key: 'コメント' })}
              />
              <FieldRow
                label="調剤数量"
                value={rp['調剤数量'].selected_option}
                isReported={rp['調剤数量'].is_reported}
                onToggle={() =>
                  reportRPUnit(rpIndex, '調剤数量', !rp['調剤数量'].is_reported)
                }
                note={reportErrorOn({ scope: 'rp', rpIndex, key: '調剤数量' })}
              />
            </Box>
          ))}
        </Box>
      )}

      {/* 依頼の段。最終行の「改善依頼」は QR 直読でも出す (項目は無くても言葉は送れる) */}
      {(basic || isQrOnly(prescription)) && (
        <Box>
          <Heading>{REQUEST_GROUP_AT_BOTTOM.heading}</Heading>
          {basic && renderGroup(REQUEST_GROUP_AT_BOTTOM, false)}
          {basic && firstRp && (
            <FieldRow
              label="医薬品漏れ"
              isReported={isMedicationMissingReported}
              onToggle={() =>
                reportRPUnit(0, '医薬品取得', !isMedicationMissingReported)
              }
              note={reportErrorOn({
                scope: 'rp',
                rpIndex: 0,
                key: '医薬品取得',
              })}
            />
          )}
          <FeedbackRow key={prescription.id} prescId={prescription.id} />
        </Box>
      )}

      <DetailFooter prescription={prescription} />
    </Box>
  )
}
